import { useMutation, useQuery } from '@tanstack/react-query';
import apiConfig from '../../../constants/apiConfig';
import axios, { AxiosError } from 'axios';
import { queryClient } from '../../../App';
import {
  selectGroupGuid,
  useAppSelector,
} from '../../../slices/commonSelectors';
import { toastConfig } from '../../../assets/data/config';
import { toast } from 'react-toastify';

export const useRedisHealth = () => {
  return useQuery({
    queryKey: ['redisHealth'],
    queryFn: async () => {
      const response = await axios.get(apiConfig.apiGateway.redisHealthCheck);
      return response.data;
    },
  });
};

export type NotificationJob = {
  id: UUID;
  short_description: string;
  notification_id: UUID;
  email_from: string;
  request_user_guid: UUID;
  repeatOptions: {
    pattern: string;
  };
  scheduler_name: string;
  job_type: string;
  job_options: {
    wind_location: UUID;
    threshold: number;
  };
  group_guid: UUID;
  active: boolean;
  aor_id: UUID;
};

type NotificationJobResponse = {
  notificationJobs: NotificationJob[];
  repeatableJobs: any[];
  oneTimeJobs: any[];
};

export const useNotificationJobs = () => {
  const groupGuid = useAppSelector(selectGroupGuid);
  return useQuery({
    queryKey: ['notificationJobs', groupGuid],
    queryFn: async () => {
      const response = await axios.get<NotificationJobResponse>(
        apiConfig.apiGateway.notification_jobs,
        {
          params: {
            group_guid: groupGuid,
          },
        }
      );
      return response.data;
    },
  });
};

export const invalidateNotificationJobs = () => {
  queryClient.invalidateQueries({
    queryKey: ['notificationJobs'],
  });
};

type CreateNotificationJobDTO = {
  short_description: string;
  group_guid: UUID;
  job_type: string;
  job_options: {
    wind_location: UUID;
    threshold: number;
  };
  repeat_options: {
    pattern: string;
  };
  notification: any;
};

type UpdateNotificationJobDTO = {
  id: UUID;
  active?: boolean;
};

export const useCreateNotificationJob = () => {
  return useMutation<
    NotificationJob,
    AxiosError<{ message: string }>,
    CreateNotificationJobDTO
  >({
    mutationFn: async (data) => {
      return await axios.post(apiConfig.apiGateway.notification_jobs, data);
    },
    onSuccess: () => {
      invalidateNotificationJobs();
    },
    onError: (error) => {
      toast.error(error.response?.data.message ?? error.message, toastConfig);
    },
  });
};

export const useUpdateNotificationJob = () => {
  return useMutation<
    NotificationJob,
    AxiosError<{ message: string }>,
    UpdateNotificationJobDTO
  >({
    mutationFn: async (data) => {
      return await axios.put(
        `${apiConfig.apiGateway.notification_jobs}/${data.id}`,
        {
          active: data.active,
        }
      );
    },
    onSuccess: () => {
      invalidateNotificationJobs();
    },
    onError: (error) => {
      toast.error(error.response?.data.message ?? error.message, toastConfig);
    },
  });
};

